/* globals __DEV__ */

import _ from "lodash";
import ko from "knockout";
import loggingService from "services/loggingService";
import { Client as IFrameClient } from "iframe-coordinator";
import Domain from "enums/Domain";
import { sessionService } from "services/sessionService";
import { isFullUrl } from "util/uriUtil";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import Promise from "bluebird";

/**
 * @param  {string} localParent localhost domain (including port) for the parent app
 * @param  {string} localChild localhost domain (including port) for the current app
 * @param  {string[]} deployOrigins the origins that the application can be deployed under
 * @param  {string} currentHost window.location.origin
 * @returns {string} the parent host for the currentHost
 */
function getParentHost(localParent, localChild, deployOrigins, currentHost) {
    switch (currentHost) {
        case localChild:
            // when we are using ifc-cli or web-directory to host locally
            return __DEV__ ? localParent : undefined;
        default:
            return deployOrigins.filter((d) => d === currentHost)[0];
    }
}

// Create deploy origins for both apps and app-regional subdomains
const appsDeployOrigins = Object.values(Domain).map(
    (baseDomain) => `https://apps.${baseDomain}`
);
const appRegionalDeployOrigins = Object.values(Domain).map(
    (baseDomain) => `https://app-regional.${baseDomain}`
);
const deployOrigins = [...appsDeployOrigins, ...appRegionalDeployOrigins];

const localParentHost = "https://localhost:3000";

const localChildHost = "https://localhost:8001";

export const hostOrigin = getParentHost(
    localParentHost,
    localChildHost,
    deployOrigins,
    window.location.origin
);

if (!hostOrigin) {
    loggingService.error(
        "failed to find an appropriate hostOrigin for the IFrameClient",
        {
            localParentHost,
            localChildHost,
        }
    );
}

const iframeClient = new IFrameClient({
    hostOrigin,
});

const envDataPromise = new Promise((resolve) => {
    iframeClient.addListener("environmentalData", (envData) => {
        //Sample payload to get timezone details from environmentalData
        // envData.custom = {
        //     timeZone: {
        //        id: "Asia/Kolkata",
        //     },
        // };
        resolve({ envData });
    });
});

iframeClient.start();

let initialized = false;
const payloadQueue = [];

function flushQueue() {
    _.each(payloadQueue, sendPayload);
}

function parseUrl(location) {
    const link = document.createElement("a");
    link.href = location;
    return link;
}

function getMessageLocation(newLocation) {
    if (isFullUrl(newLocation)) {
        const newUrl = parseUrl(newLocation);
        const currentUrl = parseUrl(window.location.href);
        if (isSamePage(currentUrl, newUrl)) {
            return newUrl.hash;
        }
    }

    return newLocation;
}

function isSamePage(url1, url2) {
    return (
        url1.protocol === url2.protocol &&
        url1.host === url2.host &&
        url1.pathname == url2.pathname &&
        url1.search === url2.search
    );
}

function queuePayload(payload) {
    payloadQueue.push(payload);
}

function sendMessage(type, data, options) {
    options = options || {};
    const message = {
        type: type,
        data: data,
        options: options,
    };

    if (initialized || message.type == "initialized") {
        sendPayload(message);
    } else {
        queuePayload(message);
    }
}

function sendPayload(payload) {
    loggingService.info("Sending message to", hostOrigin, payload);
    iframeClient.publish(payload);
}

const embeddingService = {
    currentBreadcrumbArray: ko.observableArray(),

    invalidateSession: function () {
        sendMessage("invalidatesession");
    },

    requestLogout: function () {
        sendMessage("logout");
    },

    requestModal: function (requestedModal) {
        iframeClient.requestModal(requestedModal);
    },

    requestNavigation: function (newLocation /* , options */) {
        // options = options || {};
        const location = getMessageLocation(newLocation);
        iframeClient.requestNavigation({ url: location });
    },

    requestRedirect: function (newLocation) {
        const location = getMessageLocation(newLocation);
        iframeClient.requestNavigation({ url: location });
    },

    notifyInitialized: function () {
        initialized = true;
        flushQueue();
    },

    sendToastNotification: function ({ title, message, type, ...rest }) {
        iframeClient.requestNotification({
            title,
            message,
            custom: { type, ...rest },
        });
    },

    sendPageData: function (payload) {
        const topic = "page.data";
        iframeClient.publish({ topic, payload });
    },

    sendPageBreadcrumbs: function (breadcrumbs) {
        const topic = "page.breadcrumbs";
        iframeClient.publish({ topic: topic, payload: breadcrumbs });
    },

    registerNavigation: function (navTree) {
        sendMessage("registernav", navTree);
    },

    notifyNewBreadCrumbs: function (breadcrumb) {
        if (!_.isEqual(breadcrumb, this.currentBreadcrumbArray())) {
            this.currentBreadcrumbArray(breadcrumb);
            sendMessage("breadcrumb", breadcrumb);
        }
    },

    sendEnableBreadcrumbCopyUrlIcon: function () {
        iframeClient.publish({ topic: "enableCopyUrlIcon", payload: true });
    },

    notifyHelpURL: function (helpURL) {
        sendMessage("helpurl", helpURL);
    },

    getParentHref: function () {
        let parentHref;
        if (__DEV__) {
            // in DEV environment 'window.parent.location.href' will fail the loading process due to CORS policy
            // we use window.location.href instead and hard code it to the desired one
            const childHref = window.location.href.replace(
                localChildHost,
                hostOrigin
            );
            const useNewRoute = featureToggleService.isEnabled(
                FeatureToggle.REC_5171_USE_NEW_ANALYTICS_ROUTE
            );
            if (useNewRoute) {
                // window.location.href        : 'http://localhost:8001/#/admin/someID/path'
                // window.parent.location.href : 'https://localhost:3000/#/analytics/interactions/someID/admin/path'
                const arr = childHref.split("/admin/");
                const [conversationId, ...rest] = arr[1].split("/");
                const hashPath = `/analytics/interactions/${conversationId}/admin/${rest.join(
                    "/"
                )}`;
                arr.splice(1, 1, hashPath);
                parentHref = arr.join("");
            } else {
                // window.location.href        : 'http://localhost:8001/#/admin/someID'
                // window.parent.location.href : 'http://localhost:3000/#/engage/admin/interactions/someID'
                const arr = childHref.split("/admin");
                arr.splice(1, 0, "/engage/admin/interactions");
                parentHref = arr.join("");
            }
        } else {
            parentHref = window.parent.location.href;
        }
        return parentHref;
    },

    getEnvData: function () {
        return envDataPromise;
    },

    asHostUrl(...args) {
        return iframeClient.asHostUrl(...args);
    },
};

// Request login if OAuth Session is invalid
sessionService.isOAuthSessionExpired.subscribe((isSessionExpired) => {
    if (isSessionExpired) {
        embeddingService.requestLogout();
    }
});

sessionService.isSessionValid.subscribe((isSessionValid) => {
    if (!isSessionValid) {
        embeddingService.invalidateSession();
    }
});

export default embeddingService;
